import { gql } from '@apollo/client'
import { SectionTitleStylesFragment } from 'api/graphQL/fragments/projectStyle.fragments'
import { AddressFragment } from '../fragments/address.fragments'
import { MilestoneFragments } from '../fragments/milestone.fragments'
import { UserInfoFragment } from '../fragments/userInfo.fragments'

export const ProjectItemFragments = {
    ProjectElementNodes: gql`
        fragment projectElementNodes on ProjectElementNode {
            id
            elementId
            baseElementId
            nodeOrder
            isInProject
            isDeactivated
            elementType
            text
            treePath
            parentTreePath
            parentNodeId
            projectId
            elementId
            isSearchResult
            hasChildren
            isLastSubAssembly
            isImportedToProject
            importedToProjectId
            isBasedFromImportedNode
            isBestPractice
            isGlobalBestPractice
            hasBestPractice
            isBasedFromManufacturerOption
            isProjectOnly
            isBpPublished
            attachedToFirmId
            isBpPublished
            isMasterSpecClone
            isSpecTextNode
            importedByInfo {
                importDate
                importedBy {
                    userFullName
                }
            }
            iteration {
                id
                name
            }
        }
    `,
    ProjectElementNodesWithMetadata: gql`
        fragment projectElementNodesWithMetadata on ProjectElementNode {
            id
            elementId
            baseElementId
            nodeOrder
            isInProject
            isDeactivated
            elementType
            text
            treePath
            parentTreePath
            parentNodeId
            projectId
            elementId
            isSearchResult
            hasChildren
            isLastSubAssembly
            isImportedToProject
            importedToProjectId
            isBasedFromImportedNode
            isBestPractice
            isGlobalBestPractice
            hasBestPractice
            isBasedFromManufacturerOption
            isProjectOnly
            isBpPublished
            attachedToFirmId
            isBpPublished
            isMasterSpecClone
            isSpecTextNode
            importedByInfo {
                importDate
                importedBy {
                    userFullName
                }
            }
            iteration {
                id
                name
            }
            metadata {
                updated
                modifiedBy {
                    profileInfo {
                        firstName
                        lastName
                    }
                }
            }
        }
    `,
    RecentlyViewedAndAllProjects: gql`
        fragment recentlyViewedAndAllProjects on ProjectItem {
            id
            budget
            groupName
            groupId
            imageUrl
            isFavorite
            lastUpdated
            location {
                city
                country
                postalCode
                region
                street1
                street2
            }
            currentMilestone {
                ...recentlyViewedProjectsMilestone
            }
            name
            number
            parameters {
                modifierList {
                    name
                }
                selectedModifiers {
                    name
                }
            }
            projectLeads
            owner
            grossBuildingArea
        }
        ${MilestoneFragments.RecentlyViewedProjectsMilestone}
    `,
    ProjectDetails: gql`
        fragment projectDetails on ProjectItemDetail {
            id
            firmId
            name
            number
            groupName
            owner
            grossBuildingArea
            budget
            createdDate
            imageUrl
            location {
                ...addressFragment
            }
            milestones {
                ...recentlyViewedProjectsMilestone
            }
            openBulkProcessId
            parameters {
                selectedModifiers {
                    id
                    name
                }
                modifierList {
                    id
                    name
                }
            }
            addendumNumber
            issueDate
            projectDate
            issueFor
            levelOfSpecification
        }
        ${MilestoneFragments.RecentlyViewedProjectsMilestone}
        ${AddressFragment}
    `,
    ProjectTeam: gql`
        fragment projectTeam on ProjectTeam {
            id
            assignments {
                description
                projectRole
                user {
                    ...userInfoFragment
                }
            }
        }
        ${UserInfoFragment}
    `,

    ProjectStyles: gql`
        fragment projectStyles on ProjectStyle {
            id
            name
            projectId
            overwriteMasterFormatStyles
            overwriteFontStyles
            isDefaultStyle
            isActive
            isTemplateLocked
            metadata {
                updated
                creatorId
            }
            globalStyles {
                font {
                    fontFamily
                    fontSize
                    fontStyles
                    fontColor
                }
                justification
                pageMargins {
                    bottom
                    left
                    top
                    right
                }
                spaceAfter
                spaceBefore
            }
            masterFormatOutlineStyles {
                part {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                article {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                paragraphLevel1 {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                paragraphLevel2 {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                paragraphLevel3 {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                paragraphLevel4 {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
                paragraphLevel5 {
                    displayFormat
                    fontFamily
                    fontSize
                    fontStyles
                    spaceBefore
                    spaceAfter
                    tabSpaceToParagraphNumber
                    tabSpaceToText
                    numberStyle
                    fontColor
                }
            }
            sectionStyles {
                sectionTitleStyle {
                    ...sectionTitleStylesFragment
                }
                endOfSectionStyle {
                    font {
                        fontFamily
                        fontSize
                        fontStyles
                        fontColor
                    }
                    justification
                    spaceAfter
                    spaceBefore
                    setTextToUpper
                    indentations {
                        left
                        right
                    }
                }
                omitSectionNumber
            }
            notesSpecifierStyles {
                font {
                    fontFamily
                    fontSize
                    fontStyles
                    fontColor
                }
                justification
                spaceAfter
                spaceBefore
                setTextToUpper
                indentations {
                    left
                    right
                }
            }
            unitOfMeasureStyles {
                imperial {
                    fontColor
                    fontFamily
                    fontSize
                    fontStyles
                }
                metric {
                    fontColor
                    fontFamily
                    fontSize
                    fontStyles
                }
            }
        }
        ${SectionTitleStylesFragment}
    `,

    ProjectHeaderAndFooters: gql`
        fragment projectHeaderAndFooters on ProjectHeaderAndFooter {
            isActive
            isDefault
            isTemplateLocked
            name
            headerAndFooters {
                fontSettings {
                    fontFamily
                    fontSize
                    fontStyles
                    fontColor
                }
                justification
                headerFromTop
                footerFromBottom
                mergeFields {
                    fieldName
                    actualValue
                }
                borderSettings {
                    headerFooterSection
                    headerFooterBorderType
                    borderColor
                    lineWeight
                }
            }
            isEnablePreview
            headerMergeFieldContent {
                mergeFieldFormat
                columnNumber
                justification
            }
            footerMergeFieldContent {
                mergeFieldFormat
                columnNumber
                justification
            }
            footerCopyright {
                mergeFieldFormat
                columnNumber
                justification
            }
            projectId
            id
            firmId
            metadata {
                updated
                creatorId
            }
        }
    `,

    ProductListing: gql`
        fragment productListingFragment on ProjectManufacturerProductDetail {
            firmName
            isBOD
            isSelected
            isDeactivated
            listingId
            listingText
            order
            productName
            type
            isBestPractice
            isGlobalBestPractice
            isActive
            isDeltekWriterDraft
            submissionStatus
            hoverText
        }
    `,
}
